'use client';

import { Toast as ToastPrimitive } from '@base-ui/react/toast';
import * as React from 'react';

import { FarIcons } from '@playbooks/icons';
import { Button } from '@playbooks/ui/components/button';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';
import { cn } from '@playbooks/ui/lib/utils';

function ToastProvider(props: ToastPrimitive.Provider.Props) {
	return <ToastPrimitive.Provider {...props} />;
}

function ToastViewport({ className, ...props }: ToastPrimitive.Viewport.Props) {
	// Theme
	const rootClassName = cn(
		'fixed right-4 bottom-4 z-notification mx-auto w-[calc(100vw-2rem)] sm:right-6 sm:bottom-6 sm:w-96',
		className,
	);

	// Render
	return (
		<ToastPrimitive.Portal>
			<ToastPrimitive.Viewport data-slot='toast-viewport' className={rootClassName} {...props} />
		</ToastPrimitive.Portal>
	);
}

function ToastStatusIcon({ type }: { type?: string }) {
	let icon;
	let className;

	switch (type) {
		case 'success':
			icon = FarIcons.farCircleCheck;
			className = 'text-success';
			break;
		case 'error':
			icon = FarIcons.farCircleExclamation;
			className = 'text-danger';
			break;
		case 'warning':
			icon = FarIcons.farTriangleExclamation;
			className = 'text-warning';
			break;
		case 'info':
			icon = FarIcons.farCircleInfo;
			className = 'text-foreground-nav';
			break;
		default:
			return null;
	}

	return (
		<div className='flex-middle size-5 shrink-0' aria-hidden='true'>
			<StructuralIcon icon={icon} className={className} />
		</div>
	);
}

function ToastList() {
	const { toasts } = ToastPrimitive.useToastManager();

	return toasts.map(toast => (
		<ToastPrimitive.Root
			key={toast.id}
			toast={toast}
			data-slot='toast'
			className={`[--gap:0.5rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc((var(--toast-offset-y)*-1)+(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))] [--peek:0.5rem] [--scale:calc(max(0,1-(var(--toast-index)*0.04)))] [--shrink:calc(1-var(--scale))] absolute right-0 bottom-0 z-[calc(1000-var(--toast-index))] h-[var(--height)] w-full origin-bottom rounded-media border bg-surface-raised text-foreground shadow-lg outline-none select-none [transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_200ms_cubic-bezier(0.22,1,0.36,1),opacity_150ms_ease-out,height_150ms_ease-out] after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-[''] data-ending-style:opacity-0 data-expanded:h-[var(--toast-height)] data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))] data-limited:opacity-0 data-starting-style:translate-y-8 data-starting-style:opacity-0 data-swiping:select-none [&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:translate-y-8 data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))] data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))] data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))] data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))] motion-reduce:transition-none`}>
			<ToastPrimitive.Content className='flex h-full items-start gap-3 overflow-hidden p-4 transition-opacity duration-standard ease-out data-behind:opacity-0 data-expanded:opacity-100 motion-reduce:transition-none'>
				<ToastStatusIcon type={toast.type} />
				<div className='min-w-0 grow'>
					<ToastPrimitive.Title className='text-sm font-medium text-foreground' />
					<ToastPrimitive.Description className='mt-1 text-sm text-foreground-body' />
				</div>
				<ToastPrimitive.Close
					render={
						<Button
							variant='ghost'
							size='icon-xs'
							aria-label='Dismiss notification'
							onPointerDown={event => event.preventDefault()}
						/>
					}>
					<StructuralIcon icon={FarIcons.farXmark} />
				</ToastPrimitive.Close>
			</ToastPrimitive.Content>
		</ToastPrimitive.Root>
	));
}

function Toaster() {
	return (
		<ToastViewport>
			<ToastList />
		</ToastViewport>
	);
}

const useToastManager = ToastPrimitive.useToastManager;

export { Toaster, ToastProvider, ToastViewport, useToastManager };
